import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import BilingualInput from '../../components/admin/BilingualInput';
import BilingualTextArea from '../../components/admin/BilingualTextArea';
import ClientsEditorFields from '../../components/admin/ClientsEditorFields';
import ClientLogoUploadGrid from '../../components/admin/ClientLogoUploadGrid';
import ServiceCardManager from '../../components/admin/ServiceCardManager';
import {
    Building2,
    Home,
    Type,
    PanelBottom,
    Mail,
    HardHat,
    DollarSign,
    Zap,
    LogOut,
    Loader2,
    CheckCircle2,
    Save,
    Layout,
    FileText,
    Image as ImageIcon,
    Mountain,
    MapPin,
    RefreshCw,
    AlertCircle,
    Target,
    Plane,
    Truck,
    Settings,
    Info,
    Users,
    Wrench,
    Map
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AdminTravel: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'content' | 'images'>('content');
    const [activeSection, setActiveSection] = useState<'banner' | 'about' | 'services' | 'branches' | 'clients' | 'contact'>('banner');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState<string | null>(null);
    const [content, setContent] = useState<any>({ Travel: {} });
    const [pendingImages, setPendingImages] = useState<{ [key: string]: { file: File, preview: string } }>({});
    const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' | '' }>({ text: '', type: '' });
    const [activeTravelBranches, setActiveTravelBranches] = useState<number[]>([]);
    const username = localStorage.getItem('adminUser') || 'Admin';

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
        } else {
            fetchContent();
        }
    }, [navigate]);

    const fetchContent = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/homepage/content');
            const data = await response.json();
            if (!data.Travel) data.Travel = {};
            setContent(data);
            if (data.Travel) {
                const ids = [];
                for (let i = 1; i <= 40; i++) {
                    if (data.Travel[`br_${i}_name`]) {
                        ids.push(i);
                    }
                }
                setActiveTravelBranches(ids);
            }
        } catch (err) {
            console.error('Failed to fetch content');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
    };

    const updateField = (key: string, value: any) => {
        setContent((prev: any) => ({
            ...prev,
            Travel: {
                ...prev.Travel,
                [key]: value
            }
        }));
    };

    const saveContent = async () => {
        setSaving('Travel');
        try {
            const travelData = content.Travel || {};
            const fieldsToSave: { [key: string]: { en: string, ar: string | null } } = {};

            if (activeSection === 'banner') {
                fieldsToSave['banner_title'] = { 
                    en: travelData.banner_title ?? 'Hamdan Tour And Travel',
                    ar: travelData.banner_title_ar || null
                };
                fieldsToSave['banner_subtitle'] = { 
                    en: travelData.banner_subtitle ?? 'Planning a trip today! Let us guide you the best way',
                    ar: travelData.banner_subtitle_ar || null
                };
            } else if (activeSection === 'about') {
                fieldsToSave['about_title'] = { 
                    en: travelData.about_title ?? 'Hamdan Travel & Tourism',
                    ar: travelData.about_title_ar || null
                };
                fieldsToSave['about_desc'] = { 
                    en: travelData.about_desc ?? 'With the influx of expatriates into Oman...',
                    ar: travelData.about_desc_ar || null
                };
                [1, 2, 3].forEach(id => {
                    fieldsToSave[`about_pt_${id}_title`] = { 
                        en: travelData[`about_pt_${id}_title`] ?? (id === 1 ? 'Recognized Name' : id === 2 ? 'Complete Travel Solutions' : 'Trusted by All'),
                        ar: travelData[`about_pt_${id}_title_ar`] || null
                    };
                    fieldsToSave[`about_pt_${id}_desc`] = { 
                        en: travelData[`about_pt_${id}_desc`] ?? (id === 1 ? 'Trusted IATA-accredited travel & tourism agency in Dhofar' : id === 2 ? 'Air Ticketing · Hotel Bookings · Car Rentals · Tours' : 'Serving ministries, corporates & individuals with excellence'),
                        ar: travelData[`about_pt_${id}_desc_ar`] || null
                    };
                    // Icon doesn't need Arabic
                    fieldsToSave[`about_pt_${id}_icon`] = { en: travelData[`about_pt_${id}_icon`] ?? (id === 1 ? 'Globe' : id === 2 ? 'Award' : 'MapPin'), ar: null };
                });
            } else if (activeSection === 'services') {
                fieldsToSave['services_title'] = {
                    en: travelData.services_title ?? 'Our Services',
                    ar: travelData.services_title_ar || null
                };
                [1, 2, 3].forEach(id => {
                    fieldsToSave[`svc_${id}_title`] = {
                        en: travelData[`svc_${id}_title`] ?? '',
                        ar: travelData[`svc_${id}_title_ar`] || null
                    };
                    fieldsToSave[`svc_${id}_desc`] = {
                        en: travelData[`svc_${id}_desc`] ?? '',
                        ar: travelData[`svc_${id}_desc_ar`] || null
                    };
                });
            } else if (activeSection === 'clients') {
                fieldsToSave['clients_title'] = { en: travelData.clients_title ?? 'Our Clients', ar: travelData.clients_title_ar || null };
                fieldsToSave['clients_desc'] = {
                    en: travelData.clients_desc ?? 'Corporate, government, and individual clients served by Hamdan Travel & Tourism.',
                    ar: travelData.clients_desc_ar || null
                };
                fieldsToSave['clients_list'] = { en: travelData.clients_list ?? '', ar: travelData.clients_list_ar || null };
                fieldsToSave['clients_logo_urls'] = { en: travelData.clients_logo_urls ?? '', ar: null };
                fieldsToSave['clients_footer'] = { en: travelData.clients_footer ?? '', ar: travelData.clients_footer_ar || null };
                fieldsToSave['clients_footer_desc'] = { en: travelData.clients_footer_desc ?? '', ar: travelData.clients_footer_desc_ar || null };
            } else if (activeSection === 'contact') {
                fieldsToSave['contact_address'] = { en: travelData.contact_address ?? 'P.O.Box: 190 Postal Code: 211', ar: travelData.contact_address_ar || null };
                fieldsToSave['contact_phone'] = { en: travelData.contact_phone ?? '+968 23-295011', ar: travelData.contact_phone_ar || null };
                fieldsToSave['contact_fax'] = { en: travelData.contact_fax ?? '+968 23-296746', ar: travelData.contact_fax_ar || null };
                fieldsToSave['contact_support_phone'] = { en: travelData.contact_support_phone ?? '+968 23-290884', ar: travelData.contact_support_phone_ar || null };
                fieldsToSave['contact_email'] = { en: travelData.contact_email ?? 'hamdan.travels@hamdanoman.com', ar: travelData.contact_email_ar || null };
                fieldsToSave['contact_info_desc'] = { 
                    en: travelData.contact_info_desc ?? 'We appreciate your interest in Hamdan Travel & Tourism, and we want to make it as easy as possible for you to get your queries answered.',
                    ar: travelData.contact_info_desc_ar || null
                };
            } else if (activeSection === 'branches') {
                for (let i = 1; i <= 40; i++) {
                    if (travelData[`br_${i}_name`] === undefined) continue;
                    (['name', 'addr', 'phone', 'fax', 'coord'] as string[]).forEach(f => {
                        fieldsToSave[`br_${i}_${f}`] = { en: travelData[`br_${i}_${f}`] ?? '', ar: travelData[`br_${i}_${f}_ar`] || null };
                    });
                }
            }

            const promises = Object.entries(fieldsToSave).map(async ([key, values]) => {
                const response = await fetch('/api/homepage/update-text', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: JSON.stringify({
                        section: 'Travel',
                        content_key: key,
                        content_value: values.en,
                        content_value_ar: values.ar
                    })
                });
                if (!response.ok) {
                    const errText = await response.text();
                    console.error(`Field update failed for ${key}:`, errText);
                }
                return response;
            });

            const results = await Promise.all(promises);
            const allOk = results.every(r => r.ok);
            if (allOk) {
                setMessage({ text: `${activeSection} section updated successfully!`, type: 'success' });
                fetchContent();
            } else {
                setMessage({ text: 'Some fields failed to save. Check console for details.', type: 'error' });
            }
        } catch (err) {
            console.error('Save failed', err);
            setMessage({ text: 'Failed to connect to server.', type: 'error' });
        } finally {
            setTimeout(() => {
                setSaving(null);
                setTimeout(() => setMessage({ text: '', type: '' }), 3000);
            }, 500);
        }
    };

    const handleImageSelect = (key: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPendingImages(prev => ({ ...prev, [key]: { file, preview } }));
    };

    const saveImages = async () => {
        setSaving('images');
        setMessage({ text: 'Starting image synchronization...', type: '' });
        try {
            const promises = Object.entries(pendingImages).map(async ([key, pending]) => {
                const formData = new FormData();
                formData.append('section', 'Travel');
                formData.append('content_key', key);
                formData.append('image', pending.file);
                const response = await fetch('/api/homepage/update-image', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
                    body: formData
                });
                if (!response.ok) {
                    const errText = await response.text();
                    console.error(`Upload failed for ${key}:`, errText);
                }
                return response;
            });

            const results = await Promise.all(promises);
            const allOk = results.every(r => r.ok);
            if (allOk) {
                setMessage({ text: 'All images synchronized successfully', type: 'success' });
                setPendingImages({});
                fetchContent();
            } else {
                setMessage({ text: 'Some image uploads failed.', type: 'error' });
            }
        } catch (err) {
            setMessage({ text: 'Connectivity error. Check your server status.', type: 'error' });
        } finally {
            setSaving(null);
            setTimeout(() => setMessage({ text: '', type: '' }), 4000);
        }
    };

    const sections = [
        { id: 'banner', label: 'Hero Banner', icon: Layout },
        { id: 'about', label: 'About Us', icon: FileText },
        { id: 'services', label: 'Services', icon: Wrench },
        { id: 'branches', label: 'Branch Network', icon: Map },
        { id: 'clients', label: 'Clients', icon: Users },
        { id: 'contact', label: 'Contact', icon: Mail },
    ];

    const UploadBox = ({ keyName, label, currentImage }: any) => {
        const pending = pendingImages[keyName];
        const displayImage = pending ? pending.preview : currentImage;
        return (
            <div className="group bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:border-blue-200 transition-all flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</span>
                    {currentImage && !pending && (
                        <div className="flex items-center gap-1 text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-lg text-[8px] font-bold uppercase">
                            <CheckCircle2 className="w-3 h-3" /> Live
                        </div>
                    )}
                </div>
                <div className="relative aspect-video bg-slate-50 rounded-xl overflow-hidden mb-4 border border-slate-100">
                    {displayImage ? (
                        <img src={displayImage} className="w-full h-full object-cover" alt="Preview" />
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                            <ImageIcon className="w-8 h-8 opacity-20" />
                        </div>
                    )}
                    <label className="absolute inset-0 cursor-pointer bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                        <ImageIcon className="w-6 h-6 text-white" />
                        <input type="file" className="hidden" accept="image/*" onChange={e => e.target.files?.[0] && handleImageSelect(keyName, e.target.files[0])} />
                    </label>
                </div>
                {pending && <div className="text-[8px] text-amber-500 font-bold uppercase text-center">Unsaved Changes</div>}
            </div>
        );
    };

    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            <AdminSidebar />

            {/* Main Area */}
            <div className="flex-1 flex flex-col h-full bg-slate-50/50">
                {/* Header */}
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-10 shadow-sm shrink-0 z-30">
                    <div className="flex items-center space-x-6">
                        <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                            <Plane className="w-6 h-6 text-blue-600" /> Travel & Tourism Editor
                        </h2>
                        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                            <button
                                onClick={() => setActiveTab('content')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'content' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <Type className="w-4 h-4" />
                                <span>Content Details</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('images')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'images' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>Media Assets</span>
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center space-x-6">
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-bold text-slate-900">{username}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Portal Manager</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200 shadow-inner">
                            <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-10 custom-scrollbar scroll-smooth">
                    <div className="max-w-6xl mx-auto">
                        {loading && (
                            <div className="flex items-center justify-center h-40">
                                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                            </div>
                        )}
                        <AnimatePresence mode="wait">
                            {activeTab === 'content' ? (
                                <motion.div key="content" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-8">
                                    {/* Section Navigation */}
                                    <div className="flex flex-wrap gap-3 mb-8">
                                        {sections.map(sec => (
                                            <button
                                                key={sec.id}
                                                onClick={() => setActiveSection(sec.id as any)}
                                                className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all border ${activeSection === sec.id ? 'bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                                            >
                                                <sec.icon className="w-4 h-4" />
                                                <span>{sec.label}</span>
                                            </button>
                                        ))}
                                    </div>

                                    {message.text && (
                                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`p-4 rounded-2xl font-bold text-sm flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                                            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                            {message.text}
                                        </motion.div>
                                    )}

                                    <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
                                        <div className="flex items-center justify-between mb-8">
                                            <h3 className="text-lg font-bold text-slate-800 flex items-center capitalize">
                                                <div className="w-1.5 h-6 bg-blue-600 rounded-full mr-3" />
                                                {activeSection} Configuration
                                            </h3>
                                            <button
                                                onClick={saveContent}
                                                disabled={!!saving}
                                                className="flex items-center space-x-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-xl font-bold text-sm transition-all shadow-lg active:scale-95"
                                            >
                                                {saving === 'Travel' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                                <span>Update {activeSection}</span>
                                            </button>
                                        </div>

                                        {/* === BANNER SECTION === */}
                                        {activeSection === 'banner' && (
                                            <div className="space-y-6">
                                                <BilingualInput 
                                                    label="Banner Title"
                                                    englishValue={content.Travel?.banner_title ?? 'Hamdan Tour And Travel'}
                                                    arabicValue={content.Travel?.banner_title_ar}
                                                    onChangeEnglish={val => updateField('banner_title', val)}
                                                    onChangeArabic={val => updateField('banner_title_ar', val)}
                                                />
                                                <BilingualTextArea 
                                                    label="Banner Subtitle"
                                                    englishValue={content.Travel?.banner_subtitle ?? 'Planning a trip today! Let us guide you the best way'}
                                                    arabicValue={content.Travel?.banner_subtitle_ar}
                                                    onChangeEnglish={val => updateField('banner_subtitle', val)}
                                                    onChangeArabic={val => updateField('banner_subtitle_ar', val)}
                                                    rows={3}
                                                />
                                                <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                                                    <p className="text-xs font-bold text-blue-600">💡 To update the 3 banner images, switch to the <strong>Media Assets</strong> tab above.</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* === ABOUT SECTION === */}
                                        {activeSection === 'about' && (
                                            <div className="space-y-6">
                                                <BilingualInput 
                                                    label="About Title"
                                                    englishValue={content.Travel?.about_title ?? 'Hamdan Travel & Tourism'}
                                                    arabicValue={content.Travel?.about_title_ar}
                                                    onChangeEnglish={val => updateField('about_title', val)}
                                                    onChangeArabic={val => updateField('about_title_ar', val)}
                                                />
                                                <BilingualTextArea 
                                                    label="About Description"
                                                    englishValue={content.Travel?.about_desc ?? 'With the influx of expatriates into Oman...'}
                                                    arabicValue={content.Travel?.about_desc_ar}
                                                    onChangeEnglish={val => updateField('about_desc', val)}
                                                    onChangeArabic={val => updateField('about_desc_ar', val)}
                                                    rows={6}
                                                />

                                                {/* Three Key Points */}
                                                <div className="border-t border-slate-100 pt-6">
                                                    <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-4">Key Feature Points</p>
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                                        {[
                                                            { id: 1, def_t: 'Recognized Name', def_d: 'Trusted IATA-accredited travel & tourism agency in Dhofar', def_i: 'Globe' },
                                                            { id: 2, def_t: 'Complete Travel Solutions', def_d: 'Air Ticketing · Hotel Bookings · Car Rentals · Tours', def_i: 'Award' },
                                                            { id: 3, def_t: 'Trusted by All', def_d: 'Serving ministries, corporates & individuals with excellence', def_i: 'MapPin' }
                                                        ].map(pt => (
                                                            <div key={pt.id} className="space-y-4 p-5 bg-slate-50 rounded-3xl border border-slate-100">
                                                                <div className="flex justify-between items-center mb-2">
                                                                    <span className="text-[10px] font-black text-blue-600 uppercase italic">Feature {pt.id}</span>
                                                                    <select
                                                                        className="text-[10px] font-bold rounded-lg border border-slate-200 p-1"
                                                                        value={content.Travel?.[`about_pt_${pt.id}_icon`] ?? pt.def_i}
                                                                        onChange={e => updateField(`about_pt_${pt.id}_icon`, e.target.value)}
                                                                    >
                                                                        <option value="Globe">Globe</option>
                                                                        <option value="Award">Award</option>
                                                                        <option value="MapPin">MapPin</option>
                                                                        <option value="Plane">Plane</option>
                                                                        <option value="Star">Star</option>
                                                                    </select>
                                                                </div>
                                                                <BilingualInput 
                                                                    label="Point Title"
                                                                    englishValue={content.Travel?.[`about_pt_${pt.id}_title`] ?? pt.def_t}
                                                                    arabicValue={content.Travel?.[`about_pt_${pt.id}_title_ar`]}
                                                                    onChangeEnglish={val => updateField(`about_pt_${pt.id}_title`, val)}
                                                                    onChangeArabic={val => updateField(`about_pt_${pt.id}_title_ar`, val)}
                                                                />
                                                                <BilingualTextArea 
                                                                    label="Feature Description"
                                                                    englishValue={content.Travel?.[`about_pt_${pt.id}_desc`] ?? pt.def_d}
                                                                    arabicValue={content.Travel?.[`about_pt_${pt.id}_desc_ar`]}
                                                                    onChangeEnglish={val => updateField(`about_pt_${pt.id}_desc`, val)}
                                                                    onChangeArabic={val => updateField(`about_pt_${pt.id}_desc_ar`, val)}
                                                                    rows={3}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="mt-2 p-4 bg-blue-50 rounded-xl border border-blue-100">
                                                    <p className="text-xs font-bold text-blue-600">💡 To update the About Us logo/image, switch to the <strong>Media Assets</strong> tab above.</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* === SERVICES SECTION === */}
                                        {activeSection === 'services' && (
                                            <ServiceCardManager
                                                section="Travel"
                                                data={content.Travel || {}}
                                                onSaved={fetchContent}
                                                sectionTitle={content.Travel?.services_title ?? 'Our Services'}
                                                sectionTitleAr={content.Travel?.services_title_ar}
                                                onChangeSectionTitle={val => updateField('services_title', val)}
                                                onChangeSectionTitleAr={val => updateField('services_title_ar', val)}
                                            />
                                        )}

                                        {/* === CLIENTS SECTION === */}
                                        {activeSection === 'clients' && (
                                            <ClientsEditorFields
                                                data={content.Travel}
                                                updateField={updateField}
                                                defaultDescription="Corporate, government, and individual clients served by Hamdan Travel & Tourism."
                                            />
                                        )}

                                        {/* === CONTACT SECTION === */}
                                        {activeSection === 'contact' && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                <div className="space-y-6">
                                                    <BilingualTextArea 
                                                        label="Info Description"
                                                        englishValue={content.Travel?.contact_info_desc ?? 'We appreciate your interest in Hamdan Travel & Tourism, and we want to make it as easy as possible for you to get your queries answered.'}
                                                        arabicValue={content.Travel?.contact_info_desc_ar}
                                                        onChangeEnglish={val => updateField('contact_info_desc', val)}
                                                        onChangeArabic={val => updateField('contact_info_desc_ar', val)}
                                                        rows={4}
                                                    />
                                                </div>
                                                <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 space-y-8">
                                                    <span className="text-[10px] font-black text-blue-600 uppercase">Contact Information</span>
                                                    <div className="space-y-8">
                                                        {[
                                                            { id: 'address', l: 'Address (Salalah)', d: 'P.O.Box: 190 Postal Code: 211' },
                                                            { id: 'phone', l: 'Phone (Salalah)', d: '+968 23-295011' },
                                                            { id: 'fax', l: 'Fax', d: '+968 23-296746' },
                                                            { id: 'support_phone', l: 'Support Phone', d: '+968 23-290884' },
                                                            { id: 'email', l: 'Email', d: 'hamdan.travels@hamdanoman.com' },
                                                        ].map(field => (
                                                            <BilingualInput 
                                                                key={field.id}
                                                                label={field.l}
                                                                englishValue={content.Travel?.[`contact_${field.id}`] ?? field.d}
                                                                arabicValue={content.Travel?.[`contact_${field.id}_ar`]}
                                                                onChangeEnglish={val => updateField(`contact_${field.id}`, val)}
                                                                onChangeArabic={val => updateField(`contact_${field.id}_ar`, val)}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* === BRANCHES SECTION === */}
                                        {activeSection === 'branches' && (() => {
                                            const T = content.Travel || {};
                                            const getBranches = () => {
                                                return activeTravelBranches.map(i => ({
                                                    i,
                                                    name: T[`br_${i}_name`] || '',
                                                    addr: T[`br_${i}_addr`] || '',
                                                    phone: T[`br_${i}_phone`] || '',
                                                    fax: T[`br_${i}_fax`] || '',
                                                    coord: T[`br_${i}_coord`] || ''
                                                }));
                                            };
                                            const branches = getBranches();
                                            const nextI = activeTravelBranches.length > 0 ? Math.max(...activeTravelBranches) + 1 : 1;
                                            const handleAdd = () => {
                                                updateField(`br_${nextI}_name`, 'New Branch');
                                                updateField(`br_${nextI}_addr`, '');
                                                updateField(`br_${nextI}_phone`, '');
                                                updateField(`br_${nextI}_fax`, '');
                                                updateField(`br_${nextI}_coord`, '');
                                                setActiveTravelBranches(prev => [...prev, nextI]);
                                            };
                                            const handleDelete = (idx: number) => {
                                                if (!window.confirm('Delete this branch?')) return;
                                                ['name', 'addr', 'phone', 'fax', 'coord'].forEach(f => updateField(`br_${idx}_${f}`, ''));
                                                setActiveTravelBranches(prev => prev.filter(id => id !== idx));
                                            };
                                            return (
                                                <div className="space-y-4">
                                                    <div className="flex items-center justify-between">
                                                        <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 flex-1 mr-4">
                                                            <p className="text-xs font-bold text-blue-700">{branches.length} branch(es) — Edit inline, then click "Update branches" to save.</p>
                                                        </div>
                                                        <button onClick={handleAdd} className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg">
                                                            <span className="text-lg leading-none">+</span> Add Branch
                                                        </button>
                                                    </div>
                                                    {branches.length === 0 && (
                                                        <div className="text-center py-12 text-slate-400">
                                                            <p className="text-sm font-bold">No branches configured yet.</p>
                                                            <p className="text-xs mt-1">Click "Add Branch" to get started.</p>
                                                        </div>
                                                    )}
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                        {branches.map((branch) => (
                                                            <div key={branch.i} className="p-6 rounded-2xl border border-slate-100 bg-slate-50/50 space-y-4">
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">BRANCH #{branch.i}</span>
                                                                    <button onClick={() => handleDelete(branch.i)} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-[10px] font-black transition-all">🗑 Delete</button>
                                                                </div>
                                                                <BilingualInput label="Branch Name" englishValue={T[`br_${branch.i}_name`] || ''} arabicValue={T[`br_${branch.i}_name_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_name`, val)} onChangeArabic={val => updateField(`br_${branch.i}_name_ar`, val)} />
                                                                <BilingualTextArea label="Address" englishValue={T[`br_${branch.i}_addr`] || ''} arabicValue={T[`br_${branch.i}_addr_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_addr`, val)} onChangeArabic={val => updateField(`br_${branch.i}_addr_ar`, val)} rows={2} />
                                                                <div className="grid grid-cols-2 gap-4">
                                                                    <BilingualInput label="Phone" englishValue={T[`br_${branch.i}_phone`] || ''} arabicValue={T[`br_${branch.i}_phone_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_phone`, val)} onChangeArabic={val => updateField(`br_${branch.i}_phone_ar`, val)} />
                                                                    <BilingualInput label="Fax" englishValue={T[`br_${branch.i}_fax`] || ''} arabicValue={T[`br_${branch.i}_fax_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_fax`, val)} onChangeArabic={val => updateField(`br_${branch.i}_fax_ar`, val)} />
                                                                </div>
                                                                <BilingualInput label="GPS Coordinates" englishValue={T[`br_${branch.i}_coord`] || ''} arabicValue={T[`br_${branch.i}_coord_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_coord`, val)} onChangeArabic={val => updateField(`br_${branch.i}_coord_ar`, val)} placeholder="e.g. 17.019481, 54.061263" />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div key="images" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="space-y-8">
                                    {message.text && (
                                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`p-4 rounded-2xl font-bold text-sm flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                                            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                            {message.text}
                                        </motion.div>
                                    )}
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex-1 mr-6">
                                            <h3 className="text-xl font-black text-slate-800 tracking-tight">Media Management</h3>
                                            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Global Batch Uploader</p>
                                        </div>
                                        <button
                                            onClick={saveImages}
                                            disabled={Object.keys(pendingImages).length === 0 || !!saving}
                                            className="px-10 py-5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white rounded-3xl font-black text-xs uppercase tracking-widest transition-all shadow-2xl shadow-blue-100 flex items-center space-x-3"
                                        >
                                            {saving === 'images' ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
                                            <span>Synchronize {Object.keys(pendingImages).length} Assets</span>
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        <UploadBox keyName="banner_1" label="Hero Banner 01" currentImage={content.Travel?.banner_1 ?? '/banner/travel_1.jpg'} />
                                        <UploadBox keyName="banner_2" label="Hero Banner 02" currentImage={content.Travel?.banner_2 ?? '/banner/travel_2.jpg'} />
                                        <UploadBox keyName="banner_3" label="Hero Banner 03" currentImage={content.Travel?.banner_3 ?? '/banner/travel_3.jpg'} />
                                        <UploadBox keyName="about_image" label="About Us Logo/Image" currentImage={content.Travel?.about_image ?? '/Hamdan-Travel-logo.jpg'} />
                                        <UploadBox keyName="svc_1_image" label="Service 1 Image" currentImage={content.Travel?.svc_1_image} />
                                        <UploadBox keyName="svc_2_image" label="Service 2 Image" currentImage={content.Travel?.svc_2_image} />
                                        <UploadBox keyName="svc_3_image" label="Service 3 Image" currentImage={content.Travel?.svc_3_image} />
                                    </div>

                                    <div className="mt-12">
                                        <ClientLogoUploadGrid
                                            data={content.Travel}
                                            pendingImages={pendingImages}
                                            onSelect={handleImageSelect}
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminTravel;
